/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useContext, useState } from 'react';
import { useSelector } from 'react-redux';
import { Theme, Typography, useMediaQuery } from '@mui/material';

import { Modal } from '../../../components/Modal';
import { TestIds } from '../../../testIds';
import { selectCurrentFarmerAccount } from '../../azure-b2c/storage';
import { StoreInfo } from '../../stores/storage/stores.types';
import MaterialSymbol from '../../../components/MaterialSymbol';
import { useAddressSelect } from '../../drawdown/v1/forms/AddressSelect';
import { extractZipCode, isValidZipCode } from '../../drawdown/v1/utils/extractZipCode';
import StoreSelector from '../../products/components/StockAvailability/StoreSelector';
import { constructStreetValue } from '../../../services/als';
import { CartDeliveryMode, DeliveryDetails } from '../../cart/storage/cart.types';
import {
  SelectionButton,
  SelectionContainer,
  StyledButton,
} from '../../cart/components/PickUpStoreModal';
import { SeedStoreSelector } from './SeedStoreSelect';
import { SeedStoreInfo } from '../types/SeedOrderFormType';
import { selectRemoteConfig } from '../../../services/firebase/remote-config/remoteConfig.slice';

export const SeedOrderPickUpStoreModal = () => {
  const currentAccount = useSelector(selectCurrentFarmerAccount);
  const remoteConfig = useSelector(selectRemoteConfig);

  const [value, setValue] = useState<StoreInfo | undefined>(undefined);
  const [seedStore, setSeedStore] = useState<SeedStoreInfo | undefined>(undefined);
  const [deliveryDetails] = useState<DeliveryDetails>();

  const { open, setOpen, callback } = useSeedOrderPickUpStoreModal();

  const [selectMode, setSelectMode] = React.useState<CartDeliveryMode>(CartDeliveryMode.PICKUP);

  const handleCloseModal = () => {
    setOpen(false);
  };

  const addressOptions = React.useMemo(() => {
    return {
      dateDisabled: true,
      dairyNumberEnabled: false,
      deliveryInstructions: deliveryDetails?.deliveryInstructions,
      isNewAddress: deliveryDetails?.isNewAddress,
      addressNameIsRequired: true,
    };
  }, [deliveryDetails, deliveryDetails?.isNewAddress]);

  const {
    addressShort,
    date,
    resetAddress,
    selectedAddressLine,
    selectedFullAddress,
    selectedKnownAddress,
    addressName,
    zipCode,
    setAddressError,
    deliveryInstructions,
    setDeliveryInstructions,
    AddressSelectComponent,
    setZipCode,
  } = useAddressSelect(
    currentAccount?.addresses ?? [],
    {
      city: deliveryDetails?.city ?? '',
      county: '',
      dairySupplyNum: deliveryDetails?.dairySupplyNumber ?? '',
      fullAddress: deliveryDetails?.address ?? '',
      name: deliveryDetails?.addressName ?? '',
      ruralDeliveryNum: '',
      street: deliveryDetails?.streetAddress ?? '',
      streetNumber: '',
      zipCode: deliveryDetails?.postCode ?? '',
    },
    addressOptions,
  );

  const validateAddressSelect = () => {
    if (!addressShort) {
      setAddressError('Please enter an address');
    } else {
      setAddressError(undefined);
    }
    if (isValidZipCode(zipCode, addressShort)) {
      setZipCode(extractZipCode(addressShort));
    } else {
      setAddressError('Please enter an address');
    }
    if (!addressName) {
      setAddressError('Please enter an address name');
    }
    return addressName && addressShort && isValidZipCode(zipCode, addressShort);
  };

  const callingBack = useCallback(
    (value: unknown) => {
      if (callback) {
        callback(value);
      }
    },
    [callback],
  );
  const handleAddress = useCallback(() => {
    let deliveryInfo = {};
    if (selectedAddressLine && selectedFullAddress) {
      deliveryInfo = {
        addressName: addressName,
        city: selectedFullAddress.TownCity,
        streetAddress: constructStreetValue(selectedFullAddress),
        postCode: selectedFullAddress.Postcode,
        address: addressShort,
        deliveryInstructions,
        isNewAddress: true,
      };
    } else if (selectedKnownAddress) {
      deliveryInfo = {
        addressName: selectedKnownAddress.name,
        city: selectedKnownAddress.city,
        streetAddress: selectedKnownAddress.street,
        postCode: selectedKnownAddress.zipCode,
        address: selectedKnownAddress.fullAddress,
        recordId: selectedKnownAddress.recordId,
        deliveryInstructions,
        isNewAddress: false,
      };
    }

    if (validateAddressSelect()) {
      callingBack({ ...deliveryInfo, date });
      resetAddress();
      setDeliveryInstructions('');
      setOpen(false);
    }
  }, [
    addressName,
    addressShort,
    deliveryInstructions,
    selectedAddressLine,
    selectedFullAddress,
    selectedKnownAddress,
  ]);

  const onSaveChanges = React.useCallback(() => {
    if (currentAccount) {
      if (selectMode === CartDeliveryMode.PICKUP) {
        if (value) {
          callingBack(value);
          setValue(undefined);
          setOpen(false);
        }
      } else if (selectMode === CartDeliveryMode.SEED_STORE) {
        if (seedStore) {
          callingBack(seedStore);
          setSeedStore(undefined);
          setOpen(false);
        }
      } else if (selectMode === CartDeliveryMode.DELIVERY) {
        handleAddress();
      }
    }
  }, [
    addressName,
    addressShort,
    callback,
    currentAccount,
    deliveryInstructions,
    selectMode,
    seedStore,
    selectedAddressLine,
    selectedFullAddress,
    selectedKnownAddress,
    value,
  ]);

  const footer = React.useMemo(() => {
    return (
      <StyledButton
        data-testid={TestIds.feat.pickUpStore.saveButton}
        variant='contained'
        onClick={onSaveChanges}
      >
        Save changes
      </StyledButton>
    );
  }, [onSaveChanges]);

  const renderDeliverySelector = () => {
    return <>{AddressSelectComponent}</>;
  };

  const isSm = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  return (
    <Modal
      title='Pickup or delivery?'
      open={open}
      handleClose={handleCloseModal}
      footer={footer}
      testId={TestIds.feat.pickUpStore.modal}
      maxWidth='md'
      paperProps={{
        sx: { height: isSm ? 'inherit' : '844px' },
      }}
    >
      <SelectionContainer display='inline-flex' gap='16px' mb='16px'>
        <SelectionButton
          variant='contained'
          aria-selected={selectMode === CartDeliveryMode.PICKUP}
          startIcon={<MaterialSymbol sx={{ fontWeight: 400 }}>location_on</MaterialSymbol>}
          selectMode={selectMode}
          mode={CartDeliveryMode.PICKUP}
          onClick={() => setSelectMode(CartDeliveryMode.PICKUP)}
          data-testid={TestIds.feat.seed.address.pickup}
        >
          Pickup
        </SelectionButton>

        {remoteConfig.seedStorePickupEnabled && (
          <SelectionButton
            variant='contained'
            aria-selected={selectMode === CartDeliveryMode.SEED_STORE}
            startIcon={<MaterialSymbol sx={{ fontWeight: 400 }}>location_on</MaterialSymbol>}
            selectMode={selectMode}
            mode={CartDeliveryMode.SEED_STORE}
            onClick={() => setSelectMode(CartDeliveryMode.SEED_STORE)}
            data-testid={TestIds.feat.seed.address.seedStore}
          >
            Seed store Pickup
          </SelectionButton>
        )}

        <SelectionButton
          variant='contained'
          aria-selected={selectMode === CartDeliveryMode.DELIVERY}
          startIcon={<MaterialSymbol sx={{ fontWeight: 400 }}>local_shipping</MaterialSymbol>}
          selectMode={selectMode}
          mode={CartDeliveryMode.DELIVERY}
          onClick={() => setSelectMode(CartDeliveryMode.DELIVERY)}
          data-testid={TestIds.feat.seed.address.delivery}
        >
          Delivery
        </SelectionButton>
      </SelectionContainer>
      <Typography variant='body2' mb='16px'>
        Delivery costs will be determined and confirmed during order processing.
      </Typography>
      {selectMode === CartDeliveryMode.PICKUP && (
        <StoreSelector value={value} setValue={setValue} />
      )}
      {selectMode === CartDeliveryMode.SEED_STORE && (
        <SeedStoreSelector value={seedStore} setValue={setSeedStore} />
      )}
      {selectMode === CartDeliveryMode.DELIVERY && renderDeliverySelector()}
    </Modal>
  );
};

interface SeedOrderPickUpStoreModalContextType<T> {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  callback: (item: T) => Promise<void>;
  setCallback: React.Dispatch<React.SetStateAction<(item: T) => Promise<void>>>;
}

const SeedOrderPickUpStoreModalContext = React.createContext<
  SeedOrderPickUpStoreModalContextType<any> | undefined
>(undefined);

export const SeedOrderPickUpStoreModalProvider: React.FC<{
  children: React.ReactNode | React.ReactNode[];
}> = ({ children }) => {
  const [open, setOpen] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const [callback, setCallback] = useState<(item: unknown) => Promise<void>>(() => async () => {
    //
  });

  const value = React.useMemo(() => {
    return { open, setOpen, callback, setCallback };
  }, [open, setOpen, callback, setCallback]);

  return (
    <SeedOrderPickUpStoreModalContext.Provider value={value}>
      <SeedOrderPickUpStoreModal />
      {children}
    </SeedOrderPickUpStoreModalContext.Provider>
  );
};

export const useSeedOrderPickUpStoreModal = <T,>(): SeedOrderPickUpStoreModalContextType<T> => {
  const context = useContext(SeedOrderPickUpStoreModalContext);
  if (!context) {
    throw new Error(
      'useSeedOrderPickUpStoreModal must be used within a SeedOrderPickUpStoreModalProvider',
    );
  }
  return context;
};
